import { OnboardingNotStarted } from '@/components/onboarding/OnboardingNotStarted'
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard'
import { useDriverOnboarding } from '@/hooks/queries/onboarding/useDriverOnboarding'
import { useTenantNavigation } from '@/hooks/useTenantNavigation'
import { isTerminalOnboardingPhase } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { LoaderCircleIcon } from 'lucide-react'
import { useEffect } from 'react'

export const Route = createFileRoute('/_auth/carriers/$slug/driver-onboarding/onboarding')({
  component: OnboardingPage,
  staticData: {
    title: 'route.onboarding',
  },
})

function OnboardingPage() {
  const { slug, navigate } = useTenantNavigation()
  const { data, isLoading } = useDriverOnboarding()

  // A terminal application is sent to the dashboard rather than answered here:
  // the rejected state and its reason are the status card's job, and stating it
  // in two places would let them drift (PET-3816).
  const isFinished =
    !!data?.application &&
    (data.application.reviewCompleted ||
      data.application.currentStep === 'dispatch' ||
      isTerminalOnboardingPhase(data.application.phase))

  useEffect(() => {
    if (isFinished) {
      navigate({
        to: '/carriers/$slug/driver-onboarding/dashboard',
        params: { slug },
      })
    }
  }, [isFinished, navigate, slug])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoaderCircleIcon className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const { application, driver, ...rest } = data ?? {
    insurancePrefill: null,
    vehicles: [],
    officePhone: null,
    statusSince: null,
    agreementMode: 'docuseal' as const,
  }
  // Authenticated carrier_owner_operator without a driver row yet: this is
  // the "owner will drive" flow — a separate `standard_driver_onboarding`
  // invite was emailed on carrier approval, and the driver row only exists
  // once that invite is completed.
  if (!driver) return <OnboardingNotStarted />
  if (!application) return <OnboardingNotStarted />
  if (isFinished) return null

  return <OnboardingWizard data={{ ...rest, application, driver }} />
}
